
// frontend/src/components/LOS/Caprion/CaprionAAConcentFlow.tsx
//
// Caprion AA Consent Flow — Ignosis FIU (via Caprion backend proxy)
// =================================================================
//
// Full 3-API flow:
//
//  Phase 1 — BANK SELECTION
//    GET  /api/caprion/aa/concent/fetch-banks/
//    → Returns bank_list: [{ bank_name, fip_id }]
//    → Renders a multi-select dropdown; user picks ≥1 bank(s)
//    → "Create Consent" button becomes active
//
//  Phase 2 — CREATE CONSENT
//    POST /api/caprion/aa/concent/create/
//         { bank_codes: [fip_id, …], redirect_url: <see below> }
//    → Returns { redirectionUrl, status, trackingId, referenceId }
//
//    REDIRECT BEHAVIOUR (all environments — LOCAL, UAT, PROD):
//      • redirect_url = window.location.origin + "/loan-application"
//        so Caprion sends the user back to the correct page after consent
//      • Does window.location.href = redirectionUrl (SAME-WINDOW redirect)
//      • On return, checkExistingConsent detects the "returning" flag and
//        starts background polling immediately (checking_status phase)
//
//  Phase 3 — CHECK CONSENT STATUS  (background polling)
//    GET  /api/caprion/aa/concent/status/?tracking_id=...
//    → Returns { data: { status: "INITIATED" | "ACTIVE" | "COMPLETED" | "FAILED" | … } }
//    → Polls every 5 s for a maximum of 5 minutes (60 attempts)
//
//  OUTCOME HANDLING:
//    SUCCESS  (ACTIVE / COMPLETED / APPROVED) → onDone() called after 2s
//    FAIL     (FAILED / REJECTED / EXPIRED)   → error screen + "Try Again" re-triggers journey
//    TIMEOUT  (60 polls × 5s = 5 min elapsed) → error screen + "Try Again" re-triggers journey
//
// All three Caprion API calls are proxied through the Django backend
// (the backend adds sid + checksum so the frontend never holds the secret key)

import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import JourneyHeader from "@/components/JourneyHeader";
import SegmentedProgressBar from "@/apps/los/components/ProgressBar/SegmentedProgressBar";
import JourneySectionProgress, { JourneyProgressSection } from "@/apps/los/components/ProgressBar/JourneySectionProgress";
import { useAuth } from "@/contexts/AuthContext";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface Bank {
  bank_name: string;
  fip_id: string;
}

type Phase =
  | "loading_banks"    // fetching FIP bank list
  | "select_banks"     // user picks banks from multi-select
  | "creating"         // POST createConsent in progress
  | "awaiting_consent" // new tab opened (LOCAL) or redirect triggered (UAT/PROD)
  | "checking_status"  // manual "I've completed" clicked; showing active poll UI
  | "success"          // consent ACTIVE/COMPLETED → advancing
  | "failed"           // consent FAILED/REJECTED/EXPIRED → show retry
  | "timeout"          // 3-min poll window elapsed → show retry
  | "tab_closed"       // user closed AA tab without completing (LOCAL only) → show retry
  | "error";           // unrecoverable error (API failure, etc.)

type RetryReason = "failed" | "timeout" | "tab_closed" | null;

interface Props {
  value?: any;
  onChange?: (val: any) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  onDone?: () => void;
  isBuilderMode?: boolean;
  currentStep?: number;
  totalSteps?: number;
  /** Grouped progress sections from JourneyPage; when present they replace the flat bar. */
  progressSections?: JourneyProgressSection[];
  currentPageId?: number | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS  = 5_000;
const MAX_POLL_ATTEMPTS = 60; // 60 × 5s = 5 minutes maximum

// Terminal success statuses from Caprion/Ignosis
const SUCCESS_STATUSES = new Set(["ACTIVE", "COMPLETED", "APPROVED", "OPS_VERIFICATION"]);
// Terminal failure statuses
const FAILED_STATUSES  = new Set(["FAILED", "REJECTED", "EXPIRED", "DENIED"]);

// Tab-close check interval (LOCAL only) — check every 2s whether the AA tab is still open
const TAB_CHECK_INTERVAL_MS = 2_000;

/**
 * Returns true when running on localhost / 127.0.0.1 / private IPs (local dev).
 * On UAT / APPLY / PROD the hostname will be a real domain, so this is false.
 *
 * Detects:
 * - localhost / 127.0.0.1
 * - Private IP ranges: 192.168.x.x, 10.x.x.x, 172.16-31.x.x
 *
 * NOTE: Public IP addresses (like 13.205.134.38) are treated as PRODUCTION
 * so they use same-window redirect (better for testing production flow).
 * If you want new-tab behavior on a public IP, set localStorage flag:
 *   localStorage.setItem('caprion_aa_force_new_tab', '1')
 */
const isLocalEnv = (): boolean => {
  const h = window.location.hostname;
  
  // Check force flag first
  if (localStorage.getItem('caprion_aa_force_new_tab') === '1') return true;
  
  // Explicit localhost
  if (h === "localhost" || h === "127.0.0.1") return true;
  
  // Private IP ranges only
  if (h.startsWith("192.168.") || h.startsWith("10.")) return true;
  if (h.startsWith("172.")) {
    const parts = h.split(".");
    const second = parseInt(parts[1], 10);
    if (second >= 16 && second <= 31) return true;
  }
  
  // Public IPs and domains → production flow (same-window redirect)
  return false;
};

// ─────────────────────────────────────────────────────────────────────────────
// Small helpers
// ─────────────────────────────────────────────────────────────────────────────

const Spinner: React.FC<{ className?: string }> = ({ className = "h-6 w-6" }) => (
  <svg
    className={`animate-spin text-intellend-green ${className}`}
    viewBox="0 0 24 24"
    fill="none"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// Page shell + retry screen
//
// IMPORTANT: these MUST live at module scope, not inside the component body.
// A component declared inside another component is a brand-new component *type*
// on every render, so React unmounts and remounts the entire subtree whenever
// any state changes. That destroyed the bank-search <input> DOM node on every
// keystroke, which closed the mobile keyboard after a single letter.
// ─────────────────────────────────────────────────────────────────────────────

const PageShellBase: React.FC<{
  currentStep?: number | null;
  totalSteps?: number | null;
  progressSections?: JourneyProgressSection[];
  currentPageId?: number | null;
  children: React.ReactNode;
}> = ({ currentStep, totalSteps, progressSections, currentPageId, children }) => (
  <div className="min-h-screen bg-gray-50">
    <JourneyHeader />
    {/* Progress bar — only shown when JourneyPage has passed step info.
        Prefers the grouped section bar; falls back to the flat one. */}
    {(progressSections?.length || (currentStep != null && totalSteps != null)) ? (
      <div className="w-full max-w-md mx-auto bg-white border-b px-4 py-3 shadow-sm">
        {progressSections && progressSections.length > 0 ? (
          <JourneySectionProgress
            sections={progressSections}
            currentPageId={currentPageId}
          />
        ) : (
          <SegmentedProgressBar
            currentStep={currentStep!}
            totalSteps={totalSteps!}
            isBuilderMode={false}
          />
        )}
      </div>
    ) : null}
    {children}
  </div>
);

const RetryScreenBase: React.FC<{
  icon: string;
  title: string;
  description: string;
  detail?: string | null;
  onRetry: () => void;
  currentStep?: number | null;
  totalSteps?: number | null;
  progressSections?: JourneyProgressSection[];
  currentPageId?: number | null;
}> = ({ icon, title, description, detail, onRetry, currentStep, totalSteps, progressSections, currentPageId }) => (
  <PageShellBase
    currentStep={currentStep}
    totalSteps={totalSteps}
    progressSections={progressSections}
    currentPageId={currentPageId}
  >
    <main className="container mx-auto px-4 py-8 max-w-md">
      <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-5">
        <div className="text-5xl">{icon}</div>
        <h2 className="text-xl font-bold text-red-600">{title}</h2>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left space-y-1">
          <p className="text-sm text-red-700">{description}</p>
          {detail && <p className="text-xs text-red-500 mt-1">{detail}</p>}
        </div>
        <p className="text-sm text-gray-500">
          Please select your bank(s) again and complete the Account Aggregator consent to continue.
        </p>
        <button
          type="button"
          className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 transition-colors"
          onClick={onRetry}
        >
          🔄 Try Again
        </button>
        <p className="text-sm text-center font-medium text-gray-700">
          🔒 Secured by RBI-regulated Account Aggregator framework · Powered by Ignosis
        </p>
      </div>
    </main>
  </PageShellBase>
);

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const CaprionAAConcentFlow: React.FC<Props> = ({
  onDone,
  isBuilderMode = false,
  currentStep,
  totalSteps,
  progressSections,
  currentPageId,
}) => {
  const { user } = useAuth() ?? { user: null };
  const userId = user?.id ?? null;
  // Scope localStorage keys by user ID to prevent cross-user contamination.
  // Falls back to un-scoped key only when user is not yet loaded (should not happen in practice).
  // useCallback with userId dep keeps the reference stable so downstream useCallbacks don't
  // re-fire on every render.
  const lsKey = useCallback(
    (name: string) => (userId != null ? `${name}_${userId}` : name),
    [userId],
  );
  // ── State ──────────────────────────────────────────────────────────────────
  const [phase, setPhase]                   = useState<Phase>("loading_banks");
  const [banks, setBanks]                   = useState<Bank[]>([]);
  const [selectedFipIds, setSelectedFipIds] = useState<string[]>([]);
  const [redirectionUrl, setRedirectionUrl] = useState<string | null>(null);
  const [trackingId, setTrackingId]         = useState<string | null>(null);
  const [consentStatus, setConsentStatus]   = useState<string | null>(null);
  const [error, setError]                   = useState<string | null>(null);
  const [pollCount, setPollCount]           = useState(0);
  const [retryReason, setRetryReason]       = useState<RetryReason>(null);
  const [dropdownOpen, setDropdownOpen]     = useState(false);
  const [bankSearch, setBankSearch]         = useState("");
  const [showEarlyTryAgain, setShowEarlyTryAgain] = useState(false);
  const [showTryAgainConfirm, setShowTryAgainConfirm] = useState(false);
  const [waitExtensions, setWaitExtensions] = useState(0); // how many times user chose "wait"

  // ── Refs ───────────────────────────────────────────────────────────────────
  const pollIntervalRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const tabCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tryAgainTimerRef   = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollCountRef       = useRef(0);
  const fetchedRef         = useRef(false);
  const aaTabRef           = useRef<Window | null>(null);
  const searchInputRef     = useRef<HTMLInputElement | null>(null);
  const dropdownRef        = useRef<HTMLDivElement | null>(null);
  const ctaRef             = useRef<HTMLButtonElement | null>(null);
  // Track whether we've already handled a terminal outcome (prevents double-firing)
  const terminalRef        = useRef(false);
  // tid to start polling after mount (set by checkExistingConsent on UAT/PROD return)
  const [pendingPollTid, setPendingPollTid] = useState<string | null>(null);

  // ─────────────────────────────────────────────────────────────────────────
  // Stop all background timers
  // ─────────────────────────────────────────────────────────────────────────
  const stopAllTimers = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (tabCheckIntervalRef.current) {
      clearInterval(tabCheckIntervalRef.current);
      tabCheckIntervalRef.current = null;
    }
    if (tryAgainTimerRef.current) {
      clearTimeout(tryAgainTimerRef.current);
      tryAgainTimerRef.current = null;
    }
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Handle terminal outcomes
  // ─────────────────────────────────────────────────────────────────────────
  const handleSuccess = useCallback((status: string) => {
    if (terminalRef.current) return;
    terminalRef.current = true;
    stopAllTimers();
    try { aaTabRef.current?.close(); } catch (_) {}
    setConsentStatus(status);
    setPhase("success");
    setTimeout(() => onDone?.(), 2000);
  }, [stopAllTimers, onDone]);

  const handleFailure = useCallback((reason: RetryReason, msg?: string) => {
    if (terminalRef.current) return;
    terminalRef.current = true;
    stopAllTimers();
    try { aaTabRef.current?.close(); } catch (_) {}
    setRetryReason(reason);
    if (msg) setError(msg);
    setPhase(reason ?? "error");
  }, [stopAllTimers]);

  // ─────────────────────────────────────────────────────────────────────────
  // Full reset — used by "Try Again" button
  // ─────────────────────────────────────────────────────────────────────────
  const resetAndRetry = useCallback(() => {
    stopAllTimers();
    terminalRef.current  = false;
    pollCountRef.current = 0;
    fetchedRef.current   = false;
    aaTabRef.current     = null;
    
    // Clear localStorage returning flag (only flag we still use)
    localStorage.removeItem(lsKey("caprion_aa_returning"));
    
    // Note: Backend session will be overwritten on next createConsent call
    // No need to explicitly clear it here
    
    setPhase("loading_banks");
    setError(null);
    setRetryReason(null);
    setBanks([]);
    setSelectedFipIds([]);
    setRedirectionUrl(null);
    setTrackingId(null);
    setConsentStatus(null);
    setPollCount(0);
    setShowEarlyTryAgain(false);
    setShowTryAgainConfirm(false);
    setWaitExtensions(0);
    // fetchBanks will be called by the useEffect that watches phase
  }, [stopAllTimers, lsKey]);

  // ─────────────────────────────────────────────────────────────────────────
  // Try Again confirmation dialog handlers
  // (declared AFTER resetAndRetry to avoid "used before declaration" TS error)
  // ─────────────────────────────────────────────────────────────────────────

  /** Opens the confirmation dialog instead of immediately resetting */
  const handleTryAgainClick = useCallback(() => {
    setShowTryAgainConfirm(true);
  }, []);

  /**
   * User chose "Wait for more time" — hide dialog, hide the Try Again button,
   * and schedule it to reappear after 5 more minutes.
   */
  const handleWaitMore = useCallback(() => {
    setShowTryAgainConfirm(false);
    setShowEarlyTryAgain(false);
    setWaitExtensions((n) => n + 1);
    if (tryAgainTimerRef.current) {
      clearTimeout(tryAgainTimerRef.current);
    }
    tryAgainTimerRef.current = setTimeout(() => {
      setShowEarlyTryAgain(true);
    }, 5 * 60_000);
  }, []);

  /** User confirmed restart — close dialog and reset the whole flow. */
  const handleConfirmedRestart = useCallback(() => {
    setShowTryAgainConfirm(false);
    resetAndRetry();
  }, [resetAndRetry]);

  // ─────────────────────────────────────────────────────────────────────────
  // On-mount resume check
  // Always fetches fresh tracking_id from backend DB (no caching)
  // This ensures data consistency and avoids stale session issues
  // ─────────────────────────────────────────────────────────────────────────
  const checkExistingConsent = useCallback(async (): Promise<boolean> => {
    let tid: string | null = null;
    let returning = false;

    // Fetch tracking_id from backend (always fresh from DB, no cache)
    try {
      const sessionResp = await axios.get(`${API_BASE_URL}/api/caprion/aa/concent/session/`);
      if (sessionResp.data?.has_session) {
        tid = sessionResp.data.tracking_id;
        console.info("[CaprionAAConcentFlow] Fetched tracking_id from backend DB: %s", tid);
      }
    } catch (err) {
      console.warn("[CaprionAAConcentFlow] Backend session check failed:", err);
    }

    // Check localStorage for returning flag (set before redirect)
    const returningFlag = localStorage.getItem(lsKey("caprion_aa_returning")) === "1";
    if (returningFlag) {
      localStorage.removeItem(lsKey("caprion_aa_returning"));
      returning = true;
    }

    if (!tid) {
      // No session found — proceed to bank selection
      return false;
    }

    // ── Case A: User just returned from the AA portal (UAT/PROD same-window redirect) ──
    // Caprion processes consent asynchronously — the status is typically still
    // "INITIATED" immediately after the user returns.  A single status check
    // would fall through to bank selection, which is wrong.
    // Instead, signal the useEffect to start background polling and show the
    // "checking_status" screen.  The polling loop will advance to success or
    // show a retry screen when a terminal status is reached.
    if (returning) {
      console.info(
        "[CaprionAAConcentFlow] Returning from AA portal — will start polling for tid=%s",
        tid
      );
      setTrackingId(tid);
      setPhase("checking_status");
      setPendingPollTid(tid); // triggers the useEffect below to call startPolling
      // Start 90-second early "Try Again" timer
      tryAgainTimerRef.current = setTimeout(() => {
        setShowEarlyTryAgain(true);
      }, 90_000);
      return true; // handled — don't proceed to fetchBanks
    }

    // ── Case B: Page refresh (no returning flag) — do a single BE status check ──
    // Only advance if the BE confirms a terminal success status.
    // If the DB row was deleted or status is not yet terminal → restart from bank selection.
    console.info(
      "[CaprionAAConcentFlow] Page refresh — single BE status check for tid=%s", tid
    );
    setPhase("loading_banks"); // show spinner while checking

    try {
      const res = await axios.get(`${API_BASE_URL}/api/caprion/aa/concent/status/`, {
        params: { tracking_id: tid },
      });
      const status: string =
        res.data?.data?.status ??
        res.data?.status_value ??
        res.data?.status ??
        "";
      const upper = status.toUpperCase();
      console.info("[CaprionAAConcentFlow] Refresh status check: tid=%s status=%s", tid, upper);

      if (SUCCESS_STATUSES.has(upper)) {
        // Already completed — skip the whole journey.
        terminalRef.current = true;
        setConsentStatus(status);
        setPhase("success");
        setTimeout(() => onDone?.(), 2000);
        return true;
      }

      if (FAILED_STATUSES.has(upper)) {
        // Previously failed — restart from bank selection.
        console.info("[CaprionAAConcentFlow] Refresh: terminal failure (%s) — restarting.", upper);
        return false;
      }

      // Status is in-progress (INITIATED, PROCESSING, POLLING, PENDING, etc.)
      // The user may have completed the AA journey but Caprion hasn't confirmed yet,
      // OR the user refreshed mid-journey.  Start polling so we don't lose their progress.
      console.info(
        "[CaprionAAConcentFlow] Refresh: status in-progress (%s) — starting polling.", upper
      );
      setTrackingId(tid);
      setPhase("checking_status");
      setPendingPollTid(tid);
      // Start 90-second early "Try Again" timer
      tryAgainTimerRef.current = setTimeout(() => {
        setShowEarlyTryAgain(true);
      }, 90_000);
      return true;

    } catch (err: any) {
      // 400 = DB row deleted, or other error — restart from bank selection.
      console.warn(
        "[CaprionAAConcentFlow] Refresh status check failed (HTTP %s) — restarting.",
        err?.response?.status
      );
      return false;
    }
  }, [onDone, lsKey]);

  const fetchBanks = useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setPhase("loading_banks");
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/caprion/aa/concent/fetch-banks/`);
      const bankList: Bank[] = res.data?.bank_list ?? res.data?.data?.bank_list ?? [];
      if (!bankList.length) throw new Error("No banks returned from server.");
      setBanks(bankList);
      setPhase("select_banks");
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (err instanceof Error ? err.message : String(err));
      console.error("[CaprionAAConcentFlow] fetchBanks error:", msg);
      setError(msg);
      setPhase("error");
    }
  }, []);

  useEffect(() => {
    if (isBuilderMode) return;
    // First check if there is already a completed consent in this session.
    // Only proceed to fetchBanks if there is no prior terminal success.
    checkExistingConsent().then((alreadyDone) => {
      if (!alreadyDone) fetchBanks();
    });
    return () => stopAllTimers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch banks when resetAndRetry sets phase back to "loading_banks".
  // Guard with a mounted ref so this does NOT fire on the initial render
  // (the mount useEffect above already handles the initial fetch via
  // checkExistingConsent → fetchBanks chain).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return; // skip initial render
    }
    if (phase === "loading_banks" && !fetchedRef.current && !isBuilderMode) {
      fetchBanks();
    }
  }, [phase, fetchBanks, isBuilderMode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setBankSearch("");
      }
    };
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  // ─────────────────────────────────────────────────────────────────────────
  // Phase 3 — Poll Consent Status
  // ─────────────────────────────────────────────────────────────────────────
  const pollStatus = useCallback(async (tid?: string | null) => {
    if (terminalRef.current) return;

    pollCountRef.current += 1;
    setPollCount(pollCountRef.current);

    // 5-minute timeout
    if (pollCountRef.current > MAX_POLL_ATTEMPTS) {
      handleFailure("timeout", "Consent status check timed out after 5 minutes. Please try again.");
      return;
    }

    // Use provided tid (passed from startPolling) — no need to fetch from session every time
    const resolvedTid = tid;

    try {
      const res = await axios.get(`${API_BASE_URL}/api/caprion/aa/concent/status/`, {
        params: resolvedTid ? { tracking_id: resolvedTid } : undefined,
      });

      const status: string =
        res.data?.data?.status ??
        res.data?.status_value ??
        res.data?.status ??
        "";

      console.info(`[CaprionAAConcentFlow] Poll #${pollCountRef.current}: status=${status}`);
      setConsentStatus(status);

      const upper = status.toUpperCase();
      if (SUCCESS_STATUSES.has(upper)) {
        handleSuccess(status);
      } else if (FAILED_STATUSES.has(upper)) {
        handleFailure("failed", `Consent was ${status.toLowerCase()}. Please try again.`);
      }
      // Otherwise keep polling
    } catch (err: any) {
      // Transient network error — keep polling (don't count as failure)
      console.warn("[CaprionAAConcentFlow] Poll error (will retry):", err?.message);
    }
  }, [handleSuccess, handleFailure, lsKey]);

  const startPolling = useCallback((tid?: string | null) => {
    pollCountRef.current = 0;
    setPollCount(0);
    pollStatus(tid); // immediate first call
    pollIntervalRef.current = setInterval(() => pollStatus(tid), POLL_INTERVAL_MS);
  }, [pollStatus]);

  // ─────────────────────────────────────────────────────────────────────────
  // UAT/PROD return: start polling when checkExistingConsent sets pendingPollTid.
  // Using state (not a ref) guarantees this effect fires AFTER the state update
  // and after startPolling is fully defined.
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (pendingPollTid) {
      setPendingPollTid(null);
      startPolling(pendingPollTid);
    }
  }, [pendingPollTid, startPolling]);

  // ─────────────────────────────────────────────────────────────────────────
  // Tab-close detection (LOCAL only)
  // Checks every 2s whether the AA tab has been closed by the user.
  // If closed and consent is not yet terminal → treat as "tab_closed" failure.
  // ─────────────────────────────────────────────────────────────────────────
  const startTabCloseDetection = useCallback(() => {
    tabCheckIntervalRef.current = setInterval(() => {
      if (terminalRef.current) return;
      const tab = aaTabRef.current;
      if (tab && tab.closed) {
        console.info("[CaprionAAConcentFlow] AA tab was closed by user — triggering retry");
        handleFailure("tab_closed", "It looks like you closed the AA portal without completing consent.");
      }
    }, TAB_CHECK_INTERVAL_MS);
  }, [handleFailure]);

  // ─────────────────────────────────────────────────────────────────────────
  // Phase 2 — Create Consent
  // ─────────────────────────────────────────────────────────────────────────
  const createConsent = async () => {
    if (!selectedFipIds.length) return;
    setPhase("creating");
    setError(null);
    terminalRef.current = false;

    try {
      // All environments (LOCAL, UAT, PROD) use same-window redirect.
      // redirect_url = origin + /loan-application so Caprion sends the user back to the right page.
      const redirectUrl = `${window.location.origin}/loan-application`;

      const res = await axios.post(`${API_BASE_URL}/api/caprion/aa/concent/create/`, {
        bank_codes:   selectedFipIds,
        redirect_url: redirectUrl,
      });

      const data = res.data?.data ?? res.data ?? {};
      const url: string | undefined = data.redirectionUrl ?? data.redirect_url;
      const tid: string | undefined = data.trackingId ?? data.tracking_id;

      // Backend saved tracking_id to Django session (line 397 in aa_concent.py)
      // Store tracking_id in component state (DB is source of truth)
      if (tid) {
        setTrackingId(tid);
      }

      if (url) {
        setRedirectionUrl(url);

        // Redirect the same window to the AA portal (all environments).
        // Caprion will send the user back to /loan-application when done.
        // Set a "returning" flag BEFORE navigating away so that when the
        // component re-mounts on the return redirect we know the user just
        // completed (or attempted) the AA journey and should start polling
        // immediately (checking_status phase) rather than showing bank selection.
        localStorage.setItem(lsKey("caprion_aa_returning"), "1");
        setPhase("awaiting_consent"); // brief "redirecting…" screen before navigation
        window.location.href = url;
      } else {
        // No URL returned — fall back to polling in same tab (shouldn't happen in practice)
        setPhase("awaiting_consent");
        startPolling(tid);
      }

    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (err instanceof Error ? err.message : String(err));
      console.error("[CaprionAAConcentFlow] createConsent error:", msg);
      setError(msg);
      setPhase("error");
    }
  };

  // Manual trigger: user clicked "I've completed the AA journey"
  const handleManualCheck = () => {
    setPhase("checking_status");
    // polling is already running; this just switches the UI to show the active poll view
    // Start 90-second early "Try Again" timer
    tryAgainTimerRef.current = setTimeout(() => {
      setShowEarlyTryAgain(true);
    }, 90_000);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // P1-A: Outside-click / outside-touch handler — dismiss dropdown
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setBankSearch("");
      }
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, [dropdownOpen]);

  // ─────────────────────────────────────────────────────────────────────────
  // Multi-select helpers
  // ─────────────────────────────────────────────────────────────────────────
  const toggleBank = (fipId: string) => {
    setSelectedFipIds((prev) =>
      prev.includes(fipId) ? prev.filter((id) => id !== fipId) : [...prev, fipId]
    );
    // P0-A: auto-close dropdown and clear search after selection
    setDropdownOpen(false);
    setBankSearch("");
    // P0-B: scroll CTA into view so user sees the "Continue" button immediately
    setTimeout(() => ctaRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 80);
  };

  const isSelected = (fipId: string) => selectedFipIds.includes(fipId);

  const filteredBanks = bankSearch.trim()
    ? banks.filter(
        (b) =>
          b.bank_name.toLowerCase().includes(bankSearch.toLowerCase()) ||
          b.fip_id.toLowerCase().includes(bankSearch.toLowerCase())
      )
    : banks;

  // ─────────────────────────────────────────────────────────────────────────
  // Stable wrappers around the module-level shells.
  // useCallback keeps the component *identity* stable across renders, so the
  // rendered subtree (including the bank-search input) is never remounted.
  // ─────────────────────────────────────────────────────────────────────────
  const PageShell = useCallback(
    ({ children }: { children: React.ReactNode }) => (
      <PageShellBase
        currentStep={currentStep}
        totalSteps={totalSteps}
        progressSections={progressSections}
        currentPageId={currentPageId}
      >
        {children}
      </PageShellBase>
    ),
    [currentStep, totalSteps, progressSections, currentPageId],
  );

  const RetryScreen = useCallback(
    (props: { icon: string; title: string; description: string; detail?: string | null }) => (
      <RetryScreenBase
        {...props}
        onRetry={resetAndRetry}
        currentStep={currentStep}
        totalSteps={totalSteps}
        progressSections={progressSections}
        currentPageId={currentPageId}
      />
    ),
    [resetAndRetry, currentStep, totalSteps, progressSections, currentPageId],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Builder mode preview
  // ─────────────────────────────────────────────────────────────────────────
  if (isBuilderMode) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-4 text-center text-sm text-gray-400">
        <div className="text-2xl mb-1">🏦</div>
        <p className="font-semibold text-gray-500">Caprion: AA Consent Flow</p>
        <p className="text-xs mt-1">
          3-step flow: (1) Fetch FIP banks → multi-select dropdown, (2) Create Consent →
          open AA portal (new tab LOCAL / same-window UAT+PROD), (3) Poll 3 min max.
          Handles: success → advance, fail/timeout/tab-close → retry screen.
        </p>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render: Loading banks
  // ─────────────────────────────────────────────────────────────────────────
  if (phase === "loading_banks") {
    return (
      <PageShell>
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-4">
            <div className="text-5xl">🏦</div>
            <h2 className="text-xl font-bold text-gray-800">Loading Available Banks…</h2>
            <div className="flex justify-center">
              <Spinner className="h-8 w-8" />
            </div>
            <p className="text-xs text-gray-400">Fetching FIP bank list from Caprion…</p>
          </div>
        </main>
      </PageShell>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render: Select banks (dropdown + chips)
  // ─────────────────────────────────────────────────────────────────────────
  if (phase === "select_banks") {
    return (
      <PageShell>
        {/* P0-C: Fixed CTA bar — always pinned to viewport bottom regardless of scroll container */}
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-100 shadow-[0_-2px_8px_rgba(0,0,0,0.08)] px-4 py-3">
          <div className="max-w-md mx-auto">
            <button
              ref={ctaRef}
              type="button"
              disabled={selectedFipIds.length === 0}
              onClick={createConsent}
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {selectedFipIds.length === 0
                ? "Select a bank to continue"
                : `Continue with ${selectedFipIds.length} bank${selectedFipIds.length > 1 ? "s" : ""} →`}
            </button>
          </div>
        </div>

        {/* pb-20 ensures content is not hidden behind the fixed CTA bar */}
        <main className="container mx-auto px-4 py-4 max-w-md pb-20">
          {/* P1-B: compact mobile padding */}
          <div className="bg-white shadow-md rounded-lg p-4 sm:p-8 space-y-4 sm:space-y-6">

            {/* P1-C: Compact bureau banner — inline badge instead of large block */}
            <div className="flex items-center gap-2 bg-intellend-green/5 border border-intellend-green/20 rounded-lg px-3 py-2">
              <svg className="h-4 w-4 flex-shrink-0 text-intellend-green" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-xs text-intellend-green font-semibold leading-snug">
                Credit profile verified ✓ — now checking your banking for offer assessment.
              </p>
            </div>

            {/* Header */}
            <div className="text-center space-y-2">
              <div className="text-4xl">🏦</div>
              <h2 className="text-xl font-bold text-gray-800">Select Your Bank(s)</h2>
              <p className="text-sm text-gray-500">
                Choose one or more banks to share your account data via the
                RBI-regulated Account Aggregator framework.
              </p>
            </div>

            {/* Dropdown multi-select */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-gray-700">
                Available Banks
              </label>

              {/* Dropdown trigger */}
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    const next = !dropdownOpen;
                    setDropdownOpen(next);
                    if (next) {
                      setTimeout(() => searchInputRef.current?.focus(), 50);
                    } else {
                      setBankSearch("");
                    }
                  }}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg border-2 border-gray-200 bg-white text-gray-700 hover:border-intellend-green transition-colors text-left"
                >
                  <span className="text-sm text-gray-500">
                    {selectedFipIds.length === 0
                      ? "Select bank(s)…"
                      : `${selectedFipIds.length} bank${selectedFipIds.length > 1 ? "s" : ""} selected`}
                  </span>
                  <svg
                    className={`h-4 w-4 text-gray-400 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown list with search */}
                {dropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                    {/* Search input */}
                    <div className="sticky top-0 bg-white border-b border-gray-100 px-3 py-2">
                      <div className="flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 bg-gray-50 focus-within:border-intellend-green focus-within:ring-1 focus-within:ring-intellend-green transition-colors">
                        <svg className="h-4 w-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                        </svg>
                        <input
                          ref={searchInputRef}
                          type="text"
                          value={bankSearch}
                          onChange={(e) => setBankSearch(e.target.value)}
                          placeholder="Search banks…"
                          className="flex-1 bg-transparent text-sm text-gray-700 placeholder-gray-400 outline-none"
                        />
                        {bankSearch && (
                          <button
                            type="button"
                            onClick={() => setBankSearch("")}
                            className="text-gray-400 hover:text-gray-600 transition-colors leading-none"
                            aria-label="Clear search"
                          >
                            ×
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-1 px-1">
                        {filteredBanks.length} of {banks.length} bank{banks.length !== 1 ? "s" : ""}
                        {bankSearch ? ` matching "${bankSearch}"` : ""}
                      </p>
                    </div>

                    {/* Scrollable bank list */}
                    <div className="max-h-52 overflow-y-auto">
                      {filteredBanks.length === 0 ? (
                        <div className="px-4 py-6 text-center text-sm text-gray-400">
                          No banks found for <span className="font-medium">"{bankSearch}"</span>
                        </div>
                      ) : (
                        filteredBanks.map((bank) => {
                          const selected = isSelected(bank.fip_id);
                          return (
                            <button
                              key={bank.fip_id}
                              type="button"
                              onClick={() => toggleBank(bank.fip_id)}
                              className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0 ${
                                selected ? "bg-intellend-green/5" : ""
                              }`}
                            >
                              <span
                                className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                  selected
                                    ? "border-intellend-green bg-intellend-green"
                                    : "border-gray-300 bg-white"
                                }`}
                              >
                                {selected && (
                                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-800 truncate">{bank.bank_name}</p>
                                <p className="text-xs text-gray-400 font-mono">{bank.fip_id}</p>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Selected bank chips */}
            {selectedFipIds.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Selected ({selectedFipIds.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedFipIds.map((fipId) => {
                    const bank = banks.find((b) => b.fip_id === fipId);
                    return (
                      <span
                        key={fipId}
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-intellend-green/10 text-intellend-green text-xs font-medium border border-intellend-green/20"
                      >
                        {bank?.bank_name ?? fipId}
                        <button
                          type="button"
                          onClick={() => toggleBank(fipId)}
                          className="hover:text-red-500 transition-colors leading-none"
                          aria-label={`Remove ${bank?.bank_name ?? fipId}`}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* P2: Confirmation hint — visible when bank selected and dropdown closed */}
            {selectedFipIds.length > 0 && !dropdownOpen && (
              <div className="flex items-center justify-center gap-1.5 animate-fade-in">
                <svg className="h-3.5 w-3.5 text-intellend-green flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
                <p className="text-xs text-intellend-green font-medium">
                  Tap <span className="font-bold">"Continue"</span> at the bottom to proceed
                </p>
              </div>
            )}

            <p className="text-xs text-center text-gray-400">
              🔒 Secured by RBI-regulated Account Aggregator framework · Powered by Ignosis
            </p>
          </div>
        </main>
      </PageShell>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render: Creating consent (POST in progress)
  // ─────────────────────────────────────────────────────────────────────────
  if (phase === "creating") {
    return (
      <PageShell>
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-4">
            <div className="text-5xl">🔗</div>
            <h2 className="text-xl font-bold text-gray-800">Creating Consent Request…</h2>
            <div className="flex justify-center">
              <Spinner className="h-8 w-8" />
            </div>
            <p className="text-sm text-gray-500">
              Connecting to the Account Aggregator portal. Please wait…
            </p>
          </div>
        </main>
      </PageShell>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render: Awaiting consent
  //   All environments: window.location.href already fired — show brief "redirecting…" screen
  // ─────────────────────────────────────────────────────────────────────────
  if (phase === "awaiting_consent") {
    return (
      <PageShell>
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-4">
            <div className="text-5xl">🔀</div>
            <h2 className="text-xl font-bold text-gray-800">Redirecting to AA Portal…</h2>
            <div className="flex justify-center">
              <Spinner className="h-8 w-8" />
            </div>
            <p className="text-sm text-gray-500">
              You are being redirected to the Account Aggregator portal to complete consent.
              You will be brought back automatically once done.
            </p>
          </div>
        </main>
      </PageShell>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render: Checking status (manual trigger — active poll UI)
  // ─────────────────────────────────────────────────────────────────────────
  if (phase === "checking_status") {
    return (
      <PageShell>
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-5">
            <div className="text-5xl">🔍</div>
            <h2 className="text-2xl font-bold text-gray-800">Verifying Consent Status</h2>
            <div className="flex justify-center">
              <Spinner className="h-8 w-8" />
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
              <p className="text-base font-bold text-gray-700">
                Current status:{" "}
                <span className="font-mono font-bold text-gray-900">
                  {consentStatus ?? "PENDING"}
                </span>
              </p>
              <p className="text-sm font-semibold text-gray-500">
                ⏳ Waiting for bank confirmation…
              </p>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className="bg-intellend-green h-1.5 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min((pollCount / MAX_POLL_ATTEMPTS) * 100, 100)}%` }}
                />
              </div>
            </div>
            <p className="text-base font-bold text-gray-700">
              This may take up to 5 minutes. Please do not close this page.
            </p>
            {showEarlyTryAgain && (
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500 mb-2">
                  Taking longer than expected?
                </p>
                <button
                  type="button"
                  onClick={handleTryAgainClick}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-700 rounded-lg hover:bg-green-800 transition-colors"
                >
                  Try Again
                </button>
                <p className="text-xs text-gray-400 mt-1">
                  This will restart bank selection
                </p>
              </div>
            )}

            {/* ── Confirmation dialog ── */}
            {showTryAgainConfirm && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
                  <div className="text-center space-y-2">
                    <div className="text-4xl">🔄</div>
                    <h3 className="text-lg font-bold text-gray-800">
                      Restart Account Aggregator Process?
                    </h3>
                    <p className="text-sm text-gray-500">
                      This will restart the account aggregator process. Do you want to proceed?
                    </p>
                    {waitExtensions > 0 && (
                      <p className="text-xs text-gray-400">
                        You have already waited an extra {waitExtensions * 5} minute{waitExtensions > 1 ? "s" : ""}.
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleConfirmedRestart}
                      className="w-full py-3 px-6 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors"
                    >
                      Yes, Restart
                    </button>
                    <button
                      type="button"
                      onClick={handleWaitMore}
                      className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-xl hover:bg-intellend-green/90 transition-colors"
                    >
                      ⏳ Wait 5 More Minutes
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </PageShell>
    );
  }


  // ─────────────────────────────────────────────────────────────────────────
  // Render: Success
  // ─────────────────────────────────────────────────────────────────────────
  if (phase === "success") {
    return (
      <PageShell>
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-5">
            <div className="text-6xl">✅</div>
            <h2 className="text-xl font-bold text-intellend-green">Consent Approved!</h2>
            <div className="bg-intellend-green/5 border border-intellend-green/20 rounded-lg p-4 space-y-1">
              <p className="text-sm text-intellend-green font-medium">
                Your Account Aggregator consent has been successfully granted.
              </p>
              {consentStatus && (
                <p className="text-xs text-intellend-green/70 font-mono">Status: {consentStatus}</p>
              )}
            </div>
            <div className="flex justify-center">
              <Spinner className="h-5 w-5" />
            </div>
            <p className="text-sm text-gray-500">Advancing to the next step…</p>
            <p className="text-sm text-center font-medium text-gray-700">
              🔒 Secured by RBI-regulated Account Aggregator framework · Powered by Ignosis
            </p>
          </div>
        </main>
      </PageShell>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render: Failed (consent FAILED / REJECTED / EXPIRED / DENIED)
  // ─────────────────────────────────────────────────────────────────────────
  if (phase === "failed") {
    return (
      <RetryScreen
        icon="❌"
        title="Consent Failed"
        description="Your Account Aggregator consent was rejected or could not be processed."
        detail={error}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render: Timeout (3-minute poll window elapsed)
  // ─────────────────────────────────────────────────────────────────────────
  if (phase === "timeout") {
    return (
      <RetryScreen
        icon="⏱️"
        title="Consent Timed Out"
        description="We could not confirm your consent status within 3 minutes."
        detail={error ?? "The AA portal may still be processing. Please try again."}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render: Tab closed (LOCAL only — user closed AA tab without completing)
  // ─────────────────────────────────────────────────────────────────────────
  if (phase === "tab_closed") {
    return (
      <RetryScreen
        icon="🚪"
        title="AA Portal Tab Closed"
        description="It looks like you closed the Account Aggregator portal before completing consent."
        detail={error ?? "Please try again and complete the consent process in the AA portal."}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render: Unrecoverable error
  // ─────────────────────────────────────────────────────────────────────────
  if (phase === "error") {
    return (
      <PageShell>
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-5">
            <div className="text-5xl">⚠️</div>
            <h2 className="text-xl font-bold text-red-600">Something Went Wrong</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left">
              <p className="text-sm text-red-700">{error ?? "An unexpected error occurred."}</p>
            </div>
            <button
              type="button"
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 transition-colors"
              onClick={resetAndRetry}
            >
              🔄 Try Again
            </button>
            <p className="text-sm text-center font-medium text-gray-700">
              🔒 Secured by RBI-regulated Account Aggregator framework · Powered by Ignosis
            </p>
          </div>
        </main>
      </PageShell>
    );
  }

  // Fallback (should never reach here)
  return null;
};

export default CaprionAAConcentFlow;
